import React, { useRef, RefObject, useEffect } from 'react';
import { MeetingOrganizationDetailsListType, PrivateMeetingSessionDetailsResponseType } from '../../../../lib/api';
import { TimingBlock } from './TimingBlock';
import { FooterButtons } from './FooterButtons';
import { EventType } from '../LayoutWrapper';
import { truncate } from '../helpers';
import './Card.scss';

export interface ICardProps {
  index?: number;
  event: EventType;
  changeJoinStatus: (newJoinStatus: string) => void;
  meeting: PrivateMeetingSessionDetailsResponseType;
}

const limit = 76;

const CardComponent = (props: ICardProps): JSX.Element => {
  const ref: RefObject<HTMLDivElement> | null = useRef<HTMLDivElement>(null);
  const { meeting, event, changeJoinStatus } = props;
  const { meetingId, startDateTime, endDateTime, topic,
    featured, thumbnailImageUrl, notes, presenterOrganizations } = meeting;

  const getMeetingTopic = (orgs: MeetingOrganizationDetailsListType = []): JSX.Element => {
    const meetingTopicClassName = featured ? 'font-size-20px font-semibold text-primary' : 'font-size-12px';
    const featuredMeetingTopicClassName = 'text-xl text-black mb-2.5 font-semibold';
    return (
      <p
        ref={ref}
        style={{
          display: 'block',
          overflow: 'hidden',
          wordWrap: 'break-word',
          textOverflow: 'ellipsis',
          lineHeight: featured ? '1.3em' : '1.2em',
        }}
        title={topic} className={featured ? featuredMeetingTopicClassName : meetingTopicClassName}
      >
        {topic
          ? topic
          : (orgs.length) 
            ? <span>{orgs.map(o => o.name).join(', ')}</span>
            : <span className='text-gray-500'>No meeting topic</span>}
      </p>
    );
  };

  const getMeetingPresentersList = (orgs: MeetingOrganizationDetailsListType = []): JSX.Element => {
    return <div className='font-size-12px'>
      {!!orgs.length && orgs.flatMap(o => o.attendees?.map((a) =>
        <p className='py-1' key={a.attendeeId}>
          <span className='font-semibold'>{`${a.firstName} ${a.lastName}`}</span>
          {a.title && (a.firstName || a.lastName) ? ', ' : ''}{a.title}, {o.name}
        </p>))}
    </div>;
  };

  const className = 'card-wrapper'; 
  const classNameFeatured = 'card-wrapper-featured sm:flex-row flex-col-reverse';
  useEffect(() => {
    if (ref.current && !featured) {
      const text = truncate(ref.current.textContent ?? '', limit);
      ref.current.textContent = text;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`item ${className} ${featured ? classNameFeatured : 'flex-col-reverse'}`}>
      <div
        style={{
          height: !featured ? '75%' : ''
        }}
        className={`flex flex-col justify-between ${featured ? 'md:p-4 p-8 w-2/3 sm:w-full' : 'w-full hover:bg-gray-100'}`}
      >
        <div className={`${featured ? '' : 'p-3 flex flex-col-reverse'}`}>
          {getMeetingTopic(presenterOrganizations)}
          {featured && getMeetingPresentersList(meeting.presenterOrganizations)}
          <TimingBlock
            meetingDetails={{
              startDateTime,
              endDateTime
            }}
            featured={featured}
            hideStartDate={true}
            hideEndDateTime={true}
            timeZone={event.timeZone}
            isMultiMeetingsBlock={false}
          />
        </div>
        { featured && notes
          ? <div className='flex flex-col mb-4 pr-4 word-break overflow-hidden' dangerouslySetInnerHTML={{ __html: notes }} />
          : <div style={{ height: '50px' }} />
        }
        <FooterButtons
          event={event}
          featured={featured}
          access={meeting.access}
          joinStatus={meeting.joinStatus}
          meetingId={meetingId.toString()}
          changeJoinStatus={changeJoinStatus}
          startDateTime={meeting.startDateTime}
        />
      </div>
      <div
        style={{
          height: featured ? '100%' : '25%'
        }}
        className={`bg-gray-100 ${featured ? 'sm:w-1/3 w-full' : ' w-full'}`}
      >
        {thumbnailImageUrl ? (
          <img
            width='100%'
            src={thumbnailImageUrl}
            alt='thumbnailImageUrl'
            title='thumbnailImageUrl'
            style={{ objectFit: 'cover' }}
            className={`h-full ${featured ? 'max-h-64 ' : ''}`}
          />
        ) : null}
      </div>
    </div>
  );
};

export { CardComponent };